import { BotFormContext } from "@shared/features/bots/BotForm/common/BotForm.context";
import {
  Error,
  StrategyOrder,
  SummaryData,
} from "@shared/features/bots/BotForm/common/types";
import {
  INVALID_GRIDS_DISTANCE_ERROR_MESSAGE,
  INVALID_PRICES_ERROR_MESSAGE,
  TV_BOT_ORDERS_SORTER_HEIGHT,
  ANALYTICS_FIELD_NAMES,
} from "@shared/features/bots/BotForm/common/constants";
import { useContext, useMemo } from "react";
import { OrderTypes } from "@shared/features/trades/common/types";
import { BotType, LanguageNameSpaces } from "@shared/types";
import {
  getActiveEntriesAmountError,
  getIsReverse,
} from "@shared/features/bots/BotForm/common/utils";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { translateOptionName } from "@shared/common/i18n/optionNames";

interface useEntriesSummaryProps {
  isTwoLinesSummary?: boolean;
}

const useEntriesSummary = ({
  isTwoLinesSummary,
}: useEntriesSummaryProps): SummaryData => {
  const {
    parsedEntries,
    parsedTakeProfits,
    errors,
    botType,
    isFetching,
    longShort,
    useSymbolFromAlert,
  } = useContext(BotFormContext)!;
  const isGridBot = botType === BotType.GRID;
  const isDcaBot = botType === BotType.ASAP;
  const isTradingViewSignalsBot = botType === BotType.TRADING_VIEW_SIGNALS;
  const isTradingViewBot =
    botType === BotType.TRADING_VIEW || isTradingViewSignalsBot;

  const getErrorMessageText = (error: Error) => {
    if (error === Error.MIN_ENTRY_ORDER_AMOUNT)
      return "Some of the orders amounts are lower than the minimum that is allowed in the exchange";
    else if (error === Error.ACTIVE_ENTRIES_AMOUNT)
      return getActiveEntriesAmountError(botType);
  };

  const getErrorMessage = () => {
    const errorsPriority = [
      Error.ACTIVE_ENTRIES_AMOUNT,
      Error.MIN_ENTRY_ORDER_AMOUNT,
    ];
    let amountError: string | undefined;
    for (let error of errorsPriority) {
      if (errors.includes(error)) {
        amountError = getErrorMessageText(error);
        break;
      }
    }
    let priceError: string | undefined;
    if (errors.includes(Error.ENTRY_PRICES)) {
      priceError = INVALID_PRICES_ERROR_MESSAGE;
    } else if (errors.includes(Error.CONSECUTIVE_GRIDS_PRICE_DISTANCE)) {
      priceError = INVALID_GRIDS_DISTANCE_ERROR_MESSAGE;
    }
    if (priceError && amountError) {
      return [`1. ${amountError}`, `2. ${priceError}`];
    }
    return amountError || priceError;
  };

  const { t } = useTypedTranslation(LanguageNameSpaces.BOTS);
  const title = t(ANALYTICS_FIELD_NAMES[OrderTypes.ENTRY]);
  let firstOrderDistanceText = "";
  if (isDcaBot) firstOrderDistanceText = translateOptionName("Market");
  else if (isTradingViewBot)
    firstOrderDistanceText = translateOptionName("Trigger");

  const orders = useMemo(() => {
    let orders: StrategyOrder[] | undefined = parsedEntries;
    if (parsedEntries && parsedTakeProfits && isGridBot) {
      orders = getIsReverse(longShort)
        ? [...parsedEntries, ...parsedTakeProfits]
        : [...parsedTakeProfits, ...parsedEntries];
    }
    return orders;
  }, [parsedEntries, longShort]);

  const getAmountColumnName = () => {
    if (isTradingViewSignalsBot || useSymbolFromAlert)
      return t("ordersSummary.columns.ratio");
    const baseText = `${isGridBot ? "Entry " : ""}Amount`;
    const ratioText = "(ratio)";
    return isTwoLinesSummary && isGridBot
      ? [baseText, ratioText]
      : `${baseText} ${ratioText}`;
  };

  const getPriceColumnName = () => {
    const baseText = "Price";
    const distanceText = "(distance)";
    return isTwoLinesSummary && isGridBot
      ? [baseText, distanceText]
      : `${baseText} ${distanceText}`;
  };

  return {
    key: "entries",
    title,
    orders,
    isFetching: isFetching,
    errorMessage: getErrorMessage(),
    showAmount: true,
    showPrice: isDcaBot || isGridBot,
    firstOrderDistanceText,
    orderType: OrderTypes.ENTRY,
    botType: botType,
    shouldDisplay: true,
    amountColumnName: getAmountColumnName(),
    priceColumnName: getPriceColumnName(),
    sorterHeight: isTradingViewBot ? TV_BOT_ORDERS_SORTER_HEIGHT : undefined,
  };
};

export default useEntriesSummary;
